import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import StatusPill from '../components/StatusPill';
import { usersApi, type AdminUserListItem } from '../services/authService';
import { formatApiError } from '../utils/formatApiError';

type Profile = 'ADMIN' | 'APPROVER' | 'SALES';

const PROFILES: { value: Profile; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'APPROVER', label: 'Approver' },
  { value: 'SALES', label: 'Sales' },
];

const profileColor = (p: string): 'primary' | 'warning' | 'info' | 'default' => {
  switch (p) {
    case 'ADMIN': return 'primary';
    case 'APPROVER': return 'warning';
    case 'SALES': return 'info';
    default: return 'default';
  }
};

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userOpen, setUserOpen] = useState(false);
  const [editUser, setEditUser] = useState<AdminUserListItem | null>(null);
  const [userForm, setUserForm] = useState({
    email: '',
    name: '',
    surname: '',
    password: '',
    profile: 'SALES' as Profile,
    approver_id: null as number | null,
    is_active: true,
  });
  const [showPassword, setShowPassword] = useState(false);

  const loadUsers = () => {
    setUsersLoading(true);
    usersApi.list().then(setUsers).catch(() => setUsers([])).finally(() => setUsersLoading(false));
  };

  useEffect(() => { loadUsers(); }, []);

  const approverOptions = users.filter(u => u.is_active && (u.profile === 'APPROVER' || u.profile === 'ADMIN'));

  const handleOpenCreateUser = () => {
    setEditUser(null);
    setUserForm({ email: '', name: '', surname: '', password: '', profile: 'SALES', approver_id: null, is_active: true });
    setShowPassword(false);
    setUserOpen(true);
  };

  const handleOpenEditUser = (u: AdminUserListItem) => {
    setEditUser(u);
    setUserForm({
      email: u.email || '',
      name: u.name,
      surname: u.surname,
      password: '',
      profile: (u.profile as Profile) || 'SALES',
      approver_id: u.approver_id,
      is_active: u.is_active,
    });
    setShowPassword(false);
    setUserOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      const adminRole = userForm.profile === 'ADMIN' ? 'SUPER_ADMIN' : null;
      if (editUser) {
        const payload: Record<string, any> = {
          email: userForm.email,
          name: userForm.name,
          surname: userForm.surname,
          profile: userForm.profile,
          admin_role: adminRole,
          approver_id: userForm.profile === 'SALES' ? userForm.approver_id : null,
          is_active: userForm.is_active,
        };
        if (userForm.password) payload.password = userForm.password;
        await usersApi.update(editUser.id, payload);
      } else {
        await usersApi.create({
          email: userForm.email,
          name: userForm.name,
          surname: userForm.surname,
          password: userForm.password,
          profile: userForm.profile,
          admin_role: adminRole ?? undefined,
          approver_id: userForm.profile === 'SALES' ? userForm.approver_id : null,
        });
      }
      setUserOpen(false);
      loadUsers();
    } catch (e: any) {
      alert(formatApiError(e, t('common.save')));
    }
  };

  const getApproverName = (id: number | null) => {
    if (!id) return '-';
    const u = users.find(u => u.id === id);
    return u ? `${u.name} ${u.surname}` : '-';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{t('menu.settings')}</Typography>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Users</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreateUser}>
            New User
          </Button>
        </Box>

        {usersLoading ? (
          <Typography color="text.secondary">{t('common.loading')}</Typography>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {users.map((u) => (
              <Card key={u.id} variant="outlined">
                <CardContent sx={{ pb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ flex: 1 }}>{u.name} {u.surname}</Typography>
                    <Chip label={u.profile} size="small" color={profileColor(u.profile)} />
                    <StatusPill value={u.is_active} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">{u.email}</Typography>
                  {u.profile === 'SALES' && (
                    <Typography variant="caption" color="text.secondary">
                      Approver: {u.approver_name || getApproverName(u.approver_id)}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <IconButton size="small" onClick={() => handleOpenEditUser(u)}>
                    <Edit fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Profile</TableCell>
                  <TableCell>Approver</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.name} {u.surname}</TableCell>
                    <TableCell>
                      <Chip label={u.profile} size="small" color={profileColor(u.profile)} />
                    </TableCell>
                    <TableCell>
                      {u.profile === 'SALES'
                        ? (u.approver_name || getApproverName(u.approver_id) || '-')
                        : '-'}
                    </TableCell>
                    <TableCell><StatusPill value={u.is_active} /></TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenEditUser(u)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={userOpen} onClose={() => setUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editUser ? 'Edit User' : 'New User'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth margin="dense" label="Email" type="email"
            value={userForm.email}
            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
          />
          <TextField
            fullWidth margin="dense" label="Name"
            value={userForm.name}
            onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
          />
          <TextField
            fullWidth margin="dense" label="Last Name"
            value={userForm.surname}
            onChange={(e) => setUserForm({ ...userForm, surname: e.target.value })}
          />
          <TextField
            fullWidth margin="dense"
            label={editUser ? 'New password (leave empty to keep current)' : 'Password'}
            type={showPassword ? 'text' : 'password'}
            value={userForm.password}
            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth margin="dense" select label="Profile"
            value={userForm.profile}
            onChange={(e) => setUserForm({ ...userForm, profile: e.target.value as Profile, approver_id: null })}
          >
            {PROFILES.map((p) => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
          </TextField>

          {userForm.profile === 'SALES' && (
            <TextField
              fullWidth margin="dense" select label="Approver"
              value={userForm.approver_id ?? ''}
              onChange={(e) => setUserForm({ ...userForm, approver_id: e.target.value ? Number(e.target.value) : null })}
              helperText="Select the approver for this salesperson"
            >
              <MenuItem value="">-- Select Approver --</MenuItem>
              {approverOptions.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.name} {u.surname} ({u.profile})</MenuItem>
              ))}
            </TextField>
          )}

          {editUser && (
            <FormControlLabel
              control={<Switch checked={userForm.is_active} onChange={(e) => setUserForm({ ...userForm, is_active: e.target.checked })} />}
              label="Active"
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveUser}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
